import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import api from "../services/httpService";
import "./AboutUs.css";

const AboutUs = () => {
  const [animate, setAnimate] = useState(false);
  const [sections, setSections] = useState([]);
  // const [themeColors, setThemeColors] = useState({
  //   color1: "#f83600",
  //   color2: "#fe8c00"
  // });
  const [loading, setLoading] = useState(true);
  const aboutUsRef = useRef(null);

  useEffect(() => {
    loadSections();
    fetchThemeColors();
  }, []);

  // Intersection Observer
  useEffect(() => {
    const element = aboutUsRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
        } else {
          setAnimate(false);
        }
      },
      { threshold: 0.1 }
    );
    if (element) observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  const fetchThemeColors = async () => {
    try {
      const res = await api.get("/sentinix/dashboardsettings");
      if (res.data.success && res.data.data) {
        const allData = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
        const latest = allData.sort((a, b) => b.id - a.id)[0];

        if (latest) {
          const color1 = latest.color1 || "#f83600";
          const color2 = latest.color2 || "#fe8c00";
          
          // setThemeColors({ color1, color2 });
          
          // Apply to CSS variables
          document.documentElement.style.setProperty("--theme-color1", color1);
          document.documentElement.style.setProperty("--theme-color2", color2);
        }
      }
    } catch (err) {
      console.error("Error fetching theme colors:", err);
    }
  };

  const loadSections = async () => {
    try {
      const res = await api.get("/sentinix/aboutsections");
      setSections(res.data.data || []);
    } catch (err) {
      console.error("Failed to load about sections", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper — get section by type
  const getSection = (type) =>
    sections.find((s) => s.sectionType === type);

  // Helper — get image URL safely
  const getImage = (section) => {
    if (!section?.imageKey) return "";
    if (typeof section.imageKey === "string") return section.imageKey;
    if (Array.isArray(section.imageKey)) return section.imageKey[0];
    return "";
  };

  const aboutUs = getSection("about-us");
  const content1 = getSection("content1");
  const content2 = getSection("content2");
  const content3 = getSection("content3");
 // const whyChooseUs = getSection("why-choose-us");

  if (loading) return null;

  return (
    <Box
      ref={aboutUsRef}
      className={`about-us-container ${animate ? "animate" : ""}`}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        padding: "2rem 2rem",
        paddingBottom: "0",
        textAlign: "justify",
      }}
    >
      {/* ===== Left Side: Image ===== */}
      <Box
        component="img"
        src={getImage(aboutUs)}
        alt="About Us"
        className={`about-us-image ${animate ? "animate" : ""}`}
        sx={{
          width: { xs: "78%", md: "53%" },
          height: "auto",
          // borderRadius: "10%",
          // margin: "1rem",
          // boxShadow: "0 10px 20px rgba(91, 84, 146, 0.76)",
        }}
      />

      {/* ===== Right Side: Content Tiles ===== */}
      <Box
        className="about-us-content"
        sx={{
          width: { xs: "100%", md: "45%" },
          margin: { xs: "0", md: "2rem" },
        }}
      >
        {/* Tile 1 — Who are we? */}
        {content1 && (
          <Box className="tile fade-in">
            <Typography variant="h5" gutterBottom className="about-us-title theme-gradient-text">
              {content1.title}
            </Typography>
            <Typography
              variant="body1"
              className="about-us-tile-content"
              sx={{ marginBottom: "2rem" }}
            >
              {content1.description}
            </Typography>
          </Box>
        )}

        {/* Tile 2 — Our Vision */}
        {content2 && (
          <Box className="tile fade-in">
            <Typography variant="h5" gutterBottom className="about-us-title theme-gradient-text">
              {content2.title}
            </Typography>
            <Typography
              variant="body1"
              className="about-us-tile-content"
              sx={{ marginBottom: "1rem" }}
            >
              {content2.description}
            </Typography>
          </Box>
        )}

        {/* Tile 3 — Our Mission */}
        {content3 && (
          <Box className="tile fade-in">
            <Typography variant="h5" gutterBottom className="about-us-title theme-gradient-text">
              {content3.title}
            </Typography>
            <Typography
              variant="body1"
              className="about-us-tile-content"
              sx={{ marginBottom: "1rem" }}
            >
              {content3.description}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AboutUs;